import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQrcode, faUsers, faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';

const ConnectWhatsApp = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const [qrCode, setQrCode] = useState(null);
  const [status, setStatus] = useState('initializing');
  const [error, setError] = useState('');
  const [locationName, setLocationName] = useState('');
  const [isIndividualMode, setIsIndividualMode] = useState(false);

  useEffect(() => {
    if (locationId) {
      checkLocationMode();
    }
  }, [locationId]);

  const checkLocationMode = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Check location sharing mode
      const statusResponse = await fetch(`/whatsapp/status/${locationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        
        // Check if it's individual mode
        if (statusData.sharingMode === 'individual') {
          setIsIndividualMode(true);
          setStatus('individual_mode');
          return;
        }
        
        // If shared mode and already connected
        if (statusData.connected && statusData.ready) {
          setStatus('connected');
          navigate('/dashboard/subaccounts?connected=true');
          return;
        }
      }

      // If shared mode, proceed with initialization
      initializeWhatsApp();
    } catch (err) {
      console.error('Failed to check location mode:', err);
      setError('Failed to load location settings');
      setStatus('error');
    }
  };

  const initializeWhatsApp = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      // First check if already connected
      const statusResponse = await fetch(`/whatsapp/status/${locationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        if (statusData.connected && statusData.ready) {
          setStatus('connected');
          navigate('/dashboard/subaccounts?connected=true');
          return;
        }
      }

      // Don't initialize - just fetch existing QR code
      // Backend will auto-initialize if needed
      setStatus('generating_qr');
      
      // Poll for QR code
      pollForQR();
    } catch (err) {
      console.error('Failed to check WhatsApp status:', err);
      setError('Failed to load WhatsApp connection');
      setStatus('error');
    }
  };

  const pollForQR = async () => {
    let attempts = 0;
    const maxAttempts = 30; // 30 seconds
    
    const poll = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await fetch(`/whatsapp/qr/${locationId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          
          if (data.ready) {
            setStatus('connected');
            setTimeout(() => {
              navigate('/dashboard/subaccounts?connected=true');
            }, 2000);
            return;
          }
          
          if (data.qrCode) {
            setQrCode(data.qrCode);
            setStatus('scan_qr');
            
            // Continue polling for connection
            setTimeout(() => {
              pollForConnection();
            }, 3000);
            return;
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000);
        } else {
          setError('QR code generation timed out');
          setStatus('error');
        }
      } catch (err) {
        console.error('Error polling for QR:', err);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000);
        } else {
          setError('Failed to generate QR code');
          setStatus('error');
        }
      }
    };

    poll();
  };

  const pollForConnection = async () => {
    let attempts = 0;
    const maxAttempts = 60; // 60 seconds

    const poll = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await fetch(`/whatsapp/status/${locationId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          
          if (data.connected && data.ready) {
            setStatus('connected');
            setTimeout(() => {
              navigate('/dashboard/subaccounts?connected=true');
            }, 2000);
            return;
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000);
        } else {
          // QR code might have expired, try to get a new one
          setStatus('generating_qr');
          pollForQR();
        }
      } catch (err) {
        console.error('Error polling for connection:', err);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000);
        }
      }
    };

    poll();
  };

  const retryConnection = () => {
    setQrCode(null);
    setError('');
    setStatus('initializing');
    initializeWhatsApp();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 flex items-center justify-center text-4xl mb-4">
               <FontAwesomeIcon icon={faQrcode} className="text-indigo-600" />
             </div>
             <h2 className="text-2xl font-bold text-gray-900 mb-2">
               Link WhatsApp Channel
             </h2>
             <p className="text-sm text-gray-600 mb-6">
               Location: {locationId}
             </p>

            {status === 'initializing' && (
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                 <p className="text-gray-600">Preparing your channel...</p>
              </div>
            )}

            {status === 'individual_mode' && (
              <div className="text-center">
                <div className="text-6xl mb-4"><FontAwesomeIcon icon={faUsers} className="text-indigo-600" /></div>
                 <h3 className="text-lg font-medium text-gray-900 mb-2">
                   Individual Mode
                 </h3>
                 <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                   <p className="text-sm text-blue-800 font-medium mb-2">PER-USER MODE</p>
                   <p className="text-sm text-blue-700">
                     This location is configured for per-user connections. Each team member links their own WhatsApp.
                   </p>
                 </div>
                 <p className="text-gray-600 mb-4">
                   Head to User Management to connect individual devices.
                 </p>
                 <button
                   onClick={() => navigate(`/user-management/${locationId}`)}
                   className="btn-primary"
                 >
                   Manage Users
                 </button>
              </div>
            )}

            {status === 'generating_qr' && (
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                 <p className="text-gray-600">Loading pairing code...</p>
              </div>
            )}

            {status === 'scan_qr' && qrCode && (
              <div className="text-center">
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <img 
                    src={qrCode.startsWith('data:') ? qrCode : `data:image/png;base64,${qrCode}`}
                    alt="WhatsApp QR Code" 
                    className="mx-auto max-w-xs"
                  />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                   Pair Your Device
                 </h3>
                 <div className="text-left space-y-2 text-sm text-gray-600 mb-6">
                   <div className="flex items-start">
                     <span className="bg-indigo-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-3 mt-0.5">1</span>
                     <span>Launch WhatsApp on your device</span>
                   </div>
                   <div className="flex items-start">
                     <span className="bg-indigo-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-3 mt-0.5">2</span>
                     <span>Navigate to Settings, then Linked Devices</span>
                   </div>
                   <div className="flex items-start">
                     <span className="bg-indigo-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-3 mt-0.5">3</span>
                     <span>Select Link a Device</span>
                   </div>
                   <div className="flex items-start">
                     <span className="bg-indigo-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-3 mt-0.5">4</span>
                     <span>Point your camera at the code above</span>
                   </div>
                 </div>
                 <div className="animate-pulse">
                   <p className="text-indigo-600 font-medium">Listening for connection...</p>
                 </div>
              </div>
            )}

            {status === 'connected' && (
              <div className="text-center">
                <div className="text-6xl mb-4"><FontAwesomeIcon icon={faCheckCircle} className="text-indigo-600" /></div>
                 <h3 className="text-lg font-medium text-indigo-600 mb-2">
                   Channel Linked!
                 </h3>
                 <p className="text-gray-600 mb-4">
                   WhatsApp is now routing messages for this location.
                 </p>
                <p className="text-sm text-gray-500">
                  Redirecting to dashboard...
                </p>
              </div>
            )}

            {status === 'error' && (
              <div className="text-center">
                <div className="text-6xl mb-4"><FontAwesomeIcon icon={faTimesCircle} className="text-red-600" /></div>
                <h3 className="text-lg font-medium text-red-600 mb-2">
                  Connection Failed
                </h3>
                <p className="text-gray-600 mb-4">
                  {error || 'An unexpected error occurred'}
                </p>
                <button
                  onClick={retryConnection}
                  className="btn-primary"
                >
                  Try Again
                </button>
              </div>
            )}

            <div className="mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => navigate('/dashboard/subaccounts')}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                ← Back to Locations
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectWhatsApp;
